// Panneau du bas à trois hauteurs (replié, moitié, plein écran) : la seule surface flottante sur mobile.
// On le tire par sa poignée (ou n'importe où quand il est replié) ; au clavier, la poignée est un bouton
// qui fait passer d'une hauteur à l'autre.
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import type { SheetHeight } from '../store';
import './bottom-sheet.css';

interface Props {
  height: SheetHeight;
  onHeight: (h: SheetHeight) => void;
  /** Le contenu marque d'un attribut data-apercu l'élément qui termine la partie visible une fois replié. */
  children: ReactNode;
  label: string;
  /** Change quand le contenu change de nature (accueil, fiche d'une autre maille) : la partie visible est remesurée. */
  contentKey: string;
  /** Hauteur visible en pixels, pour décaler la carte et ses boutons. */
  onVisibleHeight: (px: number) => void;
  /** Espace à laisser libre en haut en plein écran : barre d'état du téléphone, barre de sortie. */
  topInset?: number;
}

const ORDER: SheetHeight[] = ['replie', 'moitie', 'plein'];
const NAMES: Record<SheetHeight, string> = { replie: 'replié', moitie: 'à mi-hauteur', plein: 'en plein écran' };
const HANDLE = 28;
const TOP_GAP = 8;

/** Hauteur de la zone sûre en haut de l'écran (encoche, barre d'état), lue dans env(safe-area-inset-top). */
function safeAreaTop(): number {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;visibility:hidden;padding-top:env(safe-area-inset-top)';
  document.body.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return px;
}

let safeTopCache: number | null = null;
const subscribeResize = (onChange: () => void) => {
  const onResize = () => {
    safeTopCache = null; // l'encoche change de côté en tournant le téléphone
    onChange();
  };
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
};

// La coque est prérendue avec ces valeurs ; useSyncExternalStore les garde pour l'hydratation puis
// rend aussitôt les vraies mesures. Lire window dans un useState figeait la hauteur prérendue
// (React ne corrige pas un style différent à l'hydratation).
function useViewport() {
  const vh = useSyncExternalStore(subscribeResize, () => window.innerHeight, () => 800);
  const safeTop = useSyncExternalStore(subscribeResize, () => (safeTopCache ??= safeAreaTop()), () => 0);
  return { vh, safeTop };
}

export function BottomSheet({ height, onHeight, children, label, contentKey, onVisibleHeight, topInset = 0 }: Props) {
  const { vh, safeTop } = useViewport();
  const contentRef = useRef<HTMLDivElement>(null);
  const [peekH, setPeekH] = useState(220);
  const drag = useRef<{ startY: number; startVisible: number; lastY: number; lastT: number; v: number; fromHandle: boolean } | null>(null);
  const [dragVisible, setDragVisible] = useState<number | null>(null);

  // Hauteur repliée : jusqu'au bas de l'élément data-apercu, suivie quand le contenu change.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => {
      const marker = el.querySelector<HTMLElement>('[data-apercu]');
      const padding = parseFloat(getComputedStyle(el).paddingBottom) || 0;
      if (marker) setPeekH(Math.ceil(marker.offsetTop + marker.offsetHeight + padding));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    return () => observer.disconnect();
  }, [contentKey]);

  // En plein écran, le panneau s'arrête sous la barre d'état du téléphone et sous la barre de sortie.
  const full = vh - Math.max(TOP_GAP + safeTop, topInset);
  const snaps: Record<SheetHeight, number> = {
    replie: Math.min(full, peekH + HANDLE),
    moitie: Math.min(full, Math.max(peekH + HANDLE + 96, Math.round(vh * 0.56))),
    plein: full,
  };
  const target = snaps[height];
  const visible = dragVisible ?? target;

  useEffect(() => {
    // Au-delà de 60 % de l'écran, la carte ne se décale plus : elle reste lisible au-dessus.
    onVisibleHeight(Math.min(target, Math.round(vh * 0.6)));
  }, [target, vh, onVisibleHeight]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const fromHandle = (e.target as HTMLElement).closest('.feuille-poignee') != null;
    if (!fromHandle && (height !== 'replie' || (e.target as HTMLElement).closest('a, button, input, [role="slider"], [role="radiogroup"]'))) return;
    // Depuis la poignée, le pointeur sort tout de suite du panneau en montant : on le capture dès l'appui.
    if (fromHandle) e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startY: e.clientY, startVisible: visible, lastY: e.clientY, lastT: e.timeStamp, v: 0, fromHandle };
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dt = Math.max(1, e.timeStamp - d.lastT);
    d.v = (d.lastY - e.clientY) / dt;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    if (Math.abs(d.startY - e.clientY) > 6) {
      // Capture seulement une fois le glissement engagé : un simple appui reste un clic sur la poignée.
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.setPointerCapture(e.pointerId);
      setDragVisible(Math.min(full, Math.max(snaps.replie * 0.7, d.startVisible + (d.startY - e.clientY))));
    }
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (dragVisible == null) {
      // Appui sans glissement sur la poignée : hauteur suivante (le clic, capturé, n'atteint pas le bouton).
      if (d?.fromHandle) cycle();
      return;
    }
    // Un geste vif passe à la hauteur voisine ; sinon, on se cale sur la plus proche.
    const i = ORDER.indexOf(height);
    const next =
      d && Math.abs(d.v) > 0.5
        ? ORDER[Math.min(2, Math.max(0, i + (d.v > 0 ? 1 : -1)))]!
        : ORDER.reduce((best, h) => (Math.abs(snaps[h] - dragVisible) < Math.abs(snaps[best] - dragVisible) ? h : best), height);
    setDragVisible(null);
    onHeight(next);
  };

  const cycle = () => onHeight(ORDER[(ORDER.indexOf(height) + 1) % ORDER.length]!);

  /** Clavier (Entrée, Espace) : les appuis au pointeur passent par onPointerUp. */
  const onHandleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (e.detail === 0) cycle();
  };

  return (
    <section
      className={dragVisible != null ? 'feuille feuille-glisse' : 'feuille'}
      aria-label={label}
      style={{ height: full, transform: `translateY(${full - visible}px)` }}
      data-hauteur={height}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <button type="button" className="feuille-poignee" onClick={onHandleClick} aria-label={`Panneau ${NAMES[height]}. Changer la hauteur`}>
        <span aria-hidden="true" />
      </button>
      <div ref={contentRef} className="feuille-contenu" style={{ maxHeight: Math.max(0, visible - HANDLE) }}>
        {children}
      </div>
    </section>
  );
}
